import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { MobileTopBar } from '@/components/mobile/MobileTopBar';
import { MobileTabBar } from '@/layouts/mobile/MobileTabBar';
import { isLeadershipViewerTier } from '@/constants/leadership-tier';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/stores/auth.store';
import type { LeadershipTier } from '@/types/user';

export interface MobileShellContext {
  setTitle: (title: string) => void;
  setShowBack: (show: boolean) => void;
}

const TITLE_BY_PATH: Record<string, string> = {
  [ROUTES.mobile.leader.home]: '首页',
  [ROUTES.mobile.leader.team]: '团队日报',
  [ROUTES.mobile.leader.leadership]: '监管日报',
  [ROUTES.mobile.leader.projects]: '我的项目',
  [ROUTES.mobile.leader.notifications]: '消息',
  [ROUTES.mobile.leader.mine]: '我的',
  [ROUTES.mobile.leader.myReport]: '我的日报',
  [ROUTES.mobile.leader.history]: '历史日报',
  [ROUTES.mobile.leader.profile]: '个人信息',
  [ROUTES.mobile.leader.password]: '修改密码',
  [ROUTES.mobile.employee.home]: '首页',
  [ROUTES.mobile.employee.reports]: '日报',
  [ROUTES.mobile.employee.tasks]: '我的任务',
  [ROUTES.mobile.employee.notifications]: '消息',
  [ROUTES.mobile.employee.mine]: '我的',
  [ROUTES.mobile.employee.profile]: '个人信息',
  [ROUTES.mobile.employee.leader]: '直属领导',
};

const HIDE_TAB_PATHS: string[] = [
  ROUTES.mobile.leader.myReport,
  ROUTES.mobile.leader.history,
  ROUTES.mobile.leader.profile,
  ROUTES.mobile.leader.password,
  ROUTES.mobile.employee.profile,
  ROUTES.mobile.employee.leader,
];

function isLeaderDetailPath(pathname: string): boolean {
  return (
    /^\/m\/leader\/team\/[^/]+$/.test(pathname) ||
    /^\/m\/leader\/projects\/[^/]+\/reports\/(submit|[^/]+)$/.test(pathname) ||
    /^\/m\/leader\/projects\/[^/]+$/.test(pathname) ||
    /^\/m\/leader\/projects\/[^/]+\/tasks\/[^/]+$/.test(pathname) ||
    /^\/m\/leader\/notifications\/[^/]+$/.test(pathname)
  );
}

function isEmployeeDetailPath(pathname: string): boolean {
  return (
    /^\/m\/employee\/tasks\/[^/]+$/.test(pathname) ||
    /^\/m\/employee\/projects\/[^/]+\/reports\/(submit|[^/]+)$/.test(pathname) ||
    /^\/m\/employee\/projects\/[^/]+$/.test(pathname) ||
    /^\/m\/employee\/notifications\/[^/]+$/.test(pathname)
  );
}

function titleForEmployeePath(pathname: string): string | null {
  if (/^\/m\/employee\/projects\/[^/]+\/reports\/submit$/.test(pathname)) {
    return '提交项目日报';
  }
  if (/^\/m\/employee\/projects\/[^/]+\/reports\/[^/]+$/.test(pathname)) {
    return '项目日报详情';
  }
  if (/^\/m\/employee\/projects\/[^/]+$/.test(pathname)) {
    return '项目详情';
  }
  if (/^\/m\/employee\/tasks\/[^/]+$/.test(pathname)) {
    return '任务详情';
  }
  if (/^\/m\/employee\/notifications\/[^/]+$/.test(pathname)) {
    return '消息详情';
  }
  return TITLE_BY_PATH[pathname] ?? null;
}

function titleForLeaderPath(
  pathname: string,
  leadershipTier?: LeadershipTier,
): string | null {
  if (pathname === ROUTES.mobile.leader.projects && isLeadershipViewerTier(leadershipTier)) {
    return '项目进展';
  }

  if (/^\/m\/leader\/projects\/[^/]+\/reports\/submit$/.test(pathname)) {
    return '提交项目日报';
  }
  if (
    /^\/m\/leader\/projects\/[^/]+\/reports\/[^/]+$/.test(pathname) &&
    isLeadershipViewerTier(leadershipTier)
  ) {
    return '项目进展详情';
  }
  if (/^\/m\/leader\/projects\/[^/]+\/reports\/[^/]+$/.test(pathname)) {
    return '项目日报详情';
  }
  if (/^\/m\/leader\/projects\/[^/]+\/tasks\/[^/]+$/.test(pathname)) {
    return '任务详情';
  }
  if (/^\/m\/leader\/projects\/[^/]+$/.test(pathname)) {
    return '项目详情';
  }
  if (/^\/m\/leader\/team\/[^/]+$/.test(pathname)) {
    return '日报详情';
  }
  if (/^\/m\/leader\/notifications\/[^/]+$/.test(pathname)) {
    return '消息详情';
  }
  return TITLE_BY_PATH[pathname] ?? null;
}

interface MobileShellProps {
  variant: 'leader' | 'employee';
  defaultTitle: string;
}

export function MobileShell({ variant, defaultTitle }: MobileShellProps) {
  const location = useLocation();
  const leadershipTier = useAuthStore((state) => state.user?.leadershipTier);
  const isDetailPage =
    variant === 'leader'
      ? isLeaderDetailPath(location.pathname)
      : isEmployeeDetailPath(location.pathname);
  const hideTabBar = isDetailPage || HIDE_TAB_PATHS.includes(location.pathname);

  const title =
    (variant === 'leader'
      ? titleForLeaderPath(location.pathname, leadershipTier)
      : titleForEmployeePath(location.pathname)) ??
    TITLE_BY_PATH[location.pathname] ??
    defaultTitle;
  const showBack = hideTabBar;

  return (
    <div className="mobile-app">
      <div className="mobile-shell">
        <MobileTopBar title={title} showBack={showBack} />
        <main
          className={`mobile-content${hideTabBar ? ' mobile-content--no-tab' : ''}${isDetailPage ? ' mobile-content--detail' : ''}`}
        >
          <Outlet context={{ setTitle: () => {}, setShowBack: () => {} } satisfies MobileShellContext} />
        </main>
        {!hideTabBar ? <MobileTabBar variant={variant} /> : null}
      </div>
    </div>
  );
}

export function useMobileShell(): MobileShellContext {
  return useOutletContext<MobileShellContext>();
}
